// PastSessions.jsx - Past Sessions view component
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE, FRONTEND_URL } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import { useCurateBridge } from "../../hooks/useCurateBridge";
import { formatChartRangeForShare } from "../../utils/chartRangeFormatter";
import SessionForm from "../common/SessionForm";
import ScorePad from "./ScorePad";
import ChartTabsLayout from "../common/ChartTabsLayout";
import InsightCarousel from "./InsightCarousel";
import CurateSimplified from "./CurateSimplified";
import SidebarShell from "../common/SidebarShell";
import BubbleChart from "./BubbleChart";
import ActivityBubbleChart from "./ActivityBubbleChart";
import SessionCard from "./SessionCard";
import SessionInviteShareModal from "../common/SessionInviteShareModal";
import HybridShareSheet from "../common/HybridShareSheet";
import FullscreenChartModal from "../common/FullscreenChartModal";
import ConfirmModal from "./ConfirmModal";
import { filterTimelineByRange } from "../../utils/timelineRangeFilter";
import { buildImageCollage } from "../../utils/imageCollage";
import { buildSessionRecapImage } from "../../utils/sessionRecapImage";
import { buildHistoryRecapImage } from "../../utils/historyRecapImage";
import { Share2 } from "lucide-react";
import "../../styles/share-warning.css";

// Import styles
import "../../styles/profile-fixed.css";
import "../../styles/profile-responsive-enhanced.css";
import "../../styles/profile-dark-mode.css";
import "../../styles/quickdecision.css";
import "../../styles/past-sessions-charts-mobile.css";
import logger from '../../utils/logger';

// Helper function to format session date for form input
const formatSessionDate = (chosenAt) => {
  if (!chosenAt) return new Date().toISOString().split('T')[0];
  const editDate = new Date(chosenAt);
  return isNaN(editDate.getTime())
    ? new Date().toISOString().split('T')[0]
    : editDate.toISOString().split('T')[0];
};

function PastSessions({ user }) {
  const navigate = useNavigate();
  const { trackClick } = useAnalytics();

  // Curate bridge for sidebar integration
  const { setCurateRef, setInput, showSimilarGames } = useCurateBridge();
  const [showCurateSidebar, setShowCurateSidebar] = useState(false);

  const [timelineData, setTimelineData] = useState([]);
  const loadedPlayCount = useMemo(
    () => timelineData.reduce((sum, day) => sum + (day.sessions?.length || 0), 0),
    [timelineData]
  );
  const [showAllDays, setShowAllDays] = useState(false);
  const [userMap, setUserMap] = useState(new Map()); // Cache for user_id -> username mapping
  const userFetchPromisesRef = useRef({}); // Cache for in-flight user fetch promises

  // Invite status: map of session_id -> array of invite records
  const [inviteStatusMap, setInviteStatusMap] = useState({});
  // Resend share modal (from pending badge click in Timeline)
  const [resendInvite, setResendInvite] = useState(null);
  const [resendModalOpen, setResendModalOpen] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    hasMore: true
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Search + chart state
  const [sessionSearchQuery, setSessionSearchQuery] = useState("");
  const [onlyWins, setOnlyWins] = useState(false);
  const searchInputRef = useRef(null);
  const sessionsFilterRef = useRef(null);
  const winsFilterRef = useRef(null);
  const [chartRange, setChartRange] = useState("15");
  const [rangeMode, setRangeMode] = useState("sessions"); // 'sessions' | 'days'
  // Fullscreen chart modal: which chart (if any) is currently expanded
  const [fullscreenChart, setFullscreenChart] = useState(null); // null | 'bubble' | 'activity'
  const [fullscreenShareTrigger, setFullscreenShareTrigger] = useState(0);

  const openFullscreen = useCallback((chart) => {
    setFullscreenChart(chart);
    setFullscreenShareTrigger(0);
  }, []);

  const closeFullscreen = useCallback(() => {
    setFullscreenChart(null);
    setFullscreenShareTrigger(0);
  }, []);
  // Hybrid share sheet config (link vs image); null when closed
  const [hybridShareSheet, setHybridShareSheet] = useState(null);
  // Lazily fetched share slug for the current user
  const [userShareSlug, setUserShareSlug] = useState(null);

  const handleFilterWins = useCallback(() => {
    setOnlyWins((prev) => !prev);
    setTimeout(() => {
      if (winsFilterRef.current) {
        winsFilterRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (sessionsFilterRef.current) {
        sessionsFilterRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }, []);


  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: "", onConfirm: null });

  // Session logging state
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const saveInProgress = useRef(false);

  // Add button sub-menu state
  const [showAddSubMenu, setShowAddSubMenu] = useState(false);
  const [showScorePadModal, setShowScorePadModal] = useState(false);
  // Scores captured in ScorePad, handed off to SessionForm to finalize + save
  const [scorePadHandoff, setScorePadHandoff] = useState(null);

  // Banner notification state
  const [banner, setBanner] = useState({
    show: false,
    message: '',
    type: 'success' // 'success', 'error', 'info'
  });

  // Function to show banner notification
  const showBanner = (message, type = 'success') => {
    setBanner({
      show: true,
      message,
      type
    });

    // Auto-hide after 3 seconds
    setTimeout(() => {
      setBanner(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // Generate share URL for the current chart view (short link when possible)
  const ensureUserShareSlug = useCallback(async () => {
    if (userShareSlug) return userShareSlug;
    if (!user?.id) return null;
    try {
      const response = await httpRequest(`${API_BASE}/share/slug`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setUserShareSlug(data.slug);
        return data.slug;
      }
    } catch (err) {
      logger.warn('Failed to fetch user share slug:', err);
    }
    return null;
  }, [user?.id, userShareSlug]);

  const generateShareUrl = useCallback(async () => {
    const slug = await ensureUserShareSlug();
    if (slug) {
      return `${FRONTEND_URL}/h/${slug}/${chartRange}/${rangeMode}`;
    }
    // Fallback to long-form link if slug could not be fetched
    const params = new URLSearchParams({
      user: user.id,
      range: chartRange,
      mode: rangeMode,
      type: 'sessions-chart'
    });
    return `${FRONTEND_URL}/share/chart?${params.toString()}`;
  }, [user?.id, chartRange, rangeMode, ensureUserShareSlug]);

  // Calculate share statistics
  const calculateShareStats = useCallback(() => {
    if (!timelineData || !Array.isArray(timelineData)) {
      return { sessions: 0, games: 0, hours: 0 };
    }

    const filteredDays = filterTimelineByRange(timelineData, chartRange, rangeMode);

    let totalSessions = 0;
    const uniqueGames = new Set();
    let totalMinutes = 0;

    filteredDays.forEach((dayEntry) => {
      (dayEntry.sessions || []).forEach((session) => {
        if (session.game_id) {
          uniqueGames.add(session.game_id);
        }
        totalSessions += 1;
        // Default 60 minutes per session
        totalMinutes += 60;
      });
    });

    const totalHours = Math.round(totalMinutes / 60 * 10) / 10; // Round to 1 decimal

    return {
      sessions: totalSessions,
      games: uniqueGames.size,
      hours: totalHours
    };
  }, [timelineData, chartRange, rangeMode]);

  // Share handler for bubble/activity charts - opens the hybrid share sheet
  // letting the user choose between a short link and an image recap.
  const handleShareChart = useCallback(async (imageFile) => {
    const stats = calculateShareStats();
    const shareUrl = await generateShareUrl();
    const rangeText = formatChartRangeForShare(chartRange, rangeMode);
    const shareText = `My play stats in the last ${rangeText}:\n🎮 ${stats.sessions} sessions played\n🎯 ${stats.games} different games\n⏱️ ${stats.hours} hours total`;
    const caption = `${shareText}\n\nView my game history: ${shareUrl}`;

    const historyImageFile = await buildHistoryRecapImage(imageFile, stats, {
      userName: user?.username || user?.name || 'Pista User',
      rangeText,
      filename: 'history-recap.png',
    });

    setHybridShareSheet({
      title: "Share your game history",
      linkShare: { url: shareUrl, text: shareText },
      imageShare: {
        file: historyImageFile || imageFile,
        text: shareText,
        caption,
      },
      analyticsEvent: "share_history",
      analyticsMeta: { range: chartRange, mode: rangeMode },
    });
  }, [calculateShareStats, generateShareUrl, chartRange, rangeMode, user]);

  // Function to fetch user information using Supabase
  const fetchUserInfo = useCallback(async (userId) => {
    // Simple cache check - if we already have a good username, return it
    const cachedName = userMap.get(userId);
    if (cachedName && cachedName !== `User ${userId}`) {
      return cachedName;
    }

    // Prevent multiple simultaneous requests for the same user
    if (userFetchPromisesRef.current[userId]) {
      return userFetchPromisesRef.current[userId];
    }

    // Create and cache the fetch promise
    const fetchPromise = (async () => {
      try {
        // Try the most likely endpoint first
        const patterns = [
          `${API_BASE}/auth/me`,
          `${API_BASE}/users/${userId}`,
          `${API_BASE}/auth/users/${userId}`,
          `${API_BASE}/profile/${userId}`
        ];

        for (const pattern of patterns) {
          try {
            const response = await httpRequest(pattern, {
              method: "GET",
              headers: authService.getAuthHeaders(),
            });

            let username = `User ${userId}`;
            if (response.ok) {
              const data = await response.json();
              if (data) {
                if (pattern === `${API_BASE}/auth/me` && data.id === userId) {
                  username = data.username || data.name || data.display_name || `User ${userId}`;
                } else if (pattern !== `${API_BASE}/auth/me`) {
                  username = data.username || data.name || data.display_name || `User ${userId}`;
                }
              }
            }

            // Update the userMap state
            setUserMap(prev => new Map(prev.set(userId, username)));
            return username;
          } catch (err) {
            continue;
          }
        }

        // If all endpoints fail, set a default username
        setUserMap(prev => new Map(prev.set(userId, `User ${userId}`)));
        return `User ${userId}`;
      } catch (error) {
        logger.error('Error fetching user info:', error);
        setUserMap(prev => new Map(prev.set(userId, `User ${userId}`)));
        return `User ${userId}`;
      }
    })();

    userFetchPromisesRef.current[userId] = fetchPromise;
    return fetchPromise;
  }, [userMap, setUserMap]); // Include userMap dependencies

  // Extract timeline loading into a separate function
  const loadTimelineData = useCallback(async (offset = 0, append = false) => {
    if (!user?.id) return;

    try {
      // Use new endpoint for timeline data with pagination
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: offset.toString()
      });
      const url = `${API_BASE}/simplify/sessions/timeline/${user.id}?${params.toString()}`;
      const headers = authService.getAuthHeaders();

      const response = await httpRequest(url, {
        method: 'GET',
        headers: headers,
      });

      if (response.ok) {
        const response_data = await response.json();

        // Extract the timeline data from the response
        const timelineData = response_data.timeline || {};

        // Convert timeline object to array format for processing
        const data = Object.entries(timelineData).map(([date, sessions]) => ({
          date,
          display_date: new Date(date).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          sessions,
          session_count: sessions.length
        }));

        // Validate the sessions within each day
        const validatedTimeline = data.map(dayEntry => {
          const validatedSessions = dayEntry.sessions.filter(session => {
            if (!session.chosen_at) {
              return false;
            }
            return true;
          });

          return {
            ...dayEntry,
            sessions: validatedSessions
          };
        }).filter(dayEntry => dayEntry.sessions.length > 0);

        // Merge with existing timeline if appending
        if (append) {
          setTimelineData(prev => {
            const dayMap = new Map(prev.map(entry => [entry.date, entry]));
            for (const newEntry of validatedTimeline) {
              const existing = dayMap.get(newEntry.date);
              if (existing) {
                const existingIds = new Set(existing.sessions.map(s => s.id));
                const mergedSessions = [
                  ...existing.sessions,
                  ...newEntry.sessions.filter(s => !existingIds.has(s.id))
                ];
                dayMap.set(newEntry.date, {
                  ...existing,
                  sessions: mergedSessions,
                  session_count: mergedSessions.length
                });
              } else {
                dayMap.set(newEntry.date, newEntry);
              }
            }
            return Array.from(dayMap.values());
          });
        } else {
          setTimelineData(validatedTimeline);
        }

        // Fetch invite statuses for visible sessions
        const allSessions = Object.values(response_data.timeline || {}).flat();
        const sessionIds = allSessions.map(s => s.id).filter(Boolean);
        if (sessionIds.length > 0 && user?.id) {
          httpRequest(
            `${API_BASE}/invites/session?session_ids=${sessionIds.join(',')}`,
            { method: 'GET', headers: authService.getAuthHeaders() }
          ).then(r => r.ok ? r.json() : []).then(statuses => {
            const bySession = {};
            (statuses || []).forEach(inv => {
              if (!bySession[inv.session_id]) bySession[inv.session_id] = [];
              bySession[inv.session_id].push(inv);
            });
            setInviteStatusMap(prev => ({ ...prev, ...bySession }));
          }).catch(() => {});
        }

        // Update pagination state from response
        if (response_data.pagination) {
          setPagination(prev => ({
            ...prev,
            hasMore: response_data.pagination.has_more
          }));
        }
      } else {
        if (!append) {
          setTimelineData([]);
        }
      }
    } catch (error) {
      logger.error('Error loading sessions:', error);
      if (!append) {
        setTimelineData([]);
      }
    } finally {
      if (append) {
        setIsLoadingMore(false);
      }
    }
  }, [user, pagination.limit]);

  // Handle Load More click
  const handleLoadMore = () => {
    const nextOffset = pagination.offset + pagination.limit;
    setIsLoadingMore(true);
    setPagination(prev => ({
      ...prev,
      offset: nextOffset
    }));
    loadTimelineData(nextOffset, true);
  };

  // Load initial data when user or limit changes
  useEffect(() => {
    loadTimelineData();
  }, [user, pagination.limit, loadTimelineData]);

  // Reset pagination when user changes
  useEffect(() => {
    setPagination({
      limit: 50,
      offset: 0,
      hasMore: true
    });
  }, [user?.id]);

  // Pagination is now driven by handleLoadMore calling loadTimelineData directly
  // so no useEffect is needed to react to pagination.offset changes.

  // Preload user information for all participants
  useEffect(() => {
    if (timelineData.length === 0) return;

    const userIds = new Set();
    timelineData.forEach(dayEntry => {
      dayEntry.sessions.forEach(session => {
        if (session.participants) {
          session.participants.forEach(participant => {
            if (participant.user_id && !userMap.has(participant.user_id)) {
              userIds.add(participant.user_id);
            }
          });
        }
      });
    });

    // Fetch user info for all unique user_ids
    userIds.forEach(userId => {
      fetchUserInfo(userId);
    });
  }, [timelineData, fetchUserInfo, userMap]); // Include fetchUserInfo and userMap dependencies

  // Handle Log Session button click
  const handleLogSession = () => {
    // If user is not logged in, redirect to login with return URL
    if (!user) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/login?returnTo=${returnUrl}`);
      return;
    }

    // Set editing session to null for new session
    setEditingSession(null);
    setScorePadHandoff(null);
    setShowSessionModal(true);
  };

  // Handle Score & Log button click
  const handleScoreAndLog = () => {
    if (!user) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/login?returnTo=${returnUrl}`);
      return;
    }
    setScorePadHandoff(null);
    setShowScorePadModal(true);
  };

  // Show confirm dialog
  const showConfirm = (message, onConfirm) => {
    setConfirmDialog({ show: true, message, onConfirm });
  };

  // Handle Remove Session button click
  const handleRemoveSession = (session, event) => {
    event.stopPropagation(); // Prevent triggering edit session

    if (!user) return;

    // Check if user owns this session (creator user_id takes precedence over participant list)
    const isOwner = session.user_id === user.id || (
                   session.participants &&
                   session.participants.length > 0 &&
                   session.participants[0].user_id === user.id
    );

    // Check if user is a participant but not owner
    const isParticipant = session.participants &&
                         session.participants.some(p => p.user_id === user.id);

    if (!isOwner && !isParticipant) {
      showBanner('You can only remove sessions you created or leave sessions you joined.', 'error');
      return;
    }

    if (isOwner) {
      showConfirm(`Are you sure you want to remove this session of ${session.game_name}?`, async () => {
        await removeSession(session.id);
      });
    } else {
      showConfirm(`Are you sure you want to leave this session of ${session.game_name}?`, async () => {
        await leaveSession(session.id);
      });
    }
  };

  // Remove session from backend
  const removeSession = async (sessionId) => {
    try {
      trackClick('play_log_delete', { session_id: sessionId });
      const response = await httpRequest(`${API_BASE}/simplify/session/${sessionId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        showBanner('Session removed successfully', 'success');
        // Reset pagination and refresh timeline data
        setPagination({
          limit: 50,
          offset: 0,
          hasMore: true
        });
        loadTimelineData();
      } else {
        const error = await response.json();
        showBanner(`Failed to remove session: ${error.detail || error.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      logger.error('Error removing session:', error);
      showBanner('Failed to remove session. Please try again.', 'error');
    }
  };

  // Leave session from backend (for non-owner participants)
  const leaveSession = async (sessionId) => {
    try {
      trackClick('play_log_leave', { session_id: sessionId });
      const response = await httpRequest(`${API_BASE}/simplify/session/${sessionId}/leave`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        showBanner('You left the session successfully', 'success');
        // Reset pagination and refresh timeline data
        setPagination({
          limit: 50,
          offset: 0,
          hasMore: true
        });
        loadTimelineData();
      } else {
        const error = await response.json();
        showBanner(`Failed to leave session: ${error.detail || error.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      logger.error('Error leaving session:', error);
      showBanner('Failed to leave session. Please try again.', 'error');
    }
  };

  // Handle Edit Session button click
  const handleEditSession = (session) => {
    // Check if user owns this session (creator user_id takes precedence over participant list)
    const isOwner = session.user_id === user.id || (
                   session.participants &&
                   session.participants.length > 0 &&
                   session.participants[0].user_id === user.id
    );

    // Check if user is a participant but not owner
    const isParticipant = session.participants &&
                         session.participants.some(p => p.user_id === user.id);

    if (!isOwner && !isParticipant) {
      showBanner('You can only view sessions you participated in.', 'error');
      return;
    }

    // Allow both owners and participants to view the session
    // SessionForm will handle view-only mode for participants
    setEditingSession(session);
    setScorePadHandoff(null);
    setIsSaving(false); // Reset save state when opening modal
    saveInProgress.current = false; // Reset ref when opening modal
    setShowSessionModal(true);
  };

  // Function to get display name for participant - memoized to prevent unnecessary recalculations
  const getParticipantDisplayName = useCallback((participant, pIndex) => {
    // Handle system user (user_id = 0) specially
    if (participant.user_id === 0) {
      return "System";
    }

    // First, use the username from the participant data if it's valid and not a placeholder
    if (participant.username &&
        participant.username.trim() &&
        participant.username !== `Player ${pIndex + 1}` &&
        participant.username !== `User ${participant.user_id}`) {
      return participant.username;
    }

    // Next, try name field
    if (participant.name &&
        participant.name.trim() &&
        participant.name !== `Player ${pIndex + 1}` &&
        participant.name !== `User ${participant.user_id}`) {
      return participant.name;
    }

    // Next, try display_name field
    if (participant.display_name && participant.display_name.trim()) {
      return participant.display_name;
    }

    // If we have user_id, try to get cached name or fetch it
    if (participant.user_id) {
      const cachedName = userMap.get(participant.user_id);
      if (cachedName && cachedName !== `User ${participant.user_id}`) {
        return cachedName;
      }

      // Trigger fetch in background (only if we don't have a valid cached name)
      fetchUserInfo(participant.user_id).then(username => {
        if (username && username !== `User ${participant.user_id}`) {
          setUserMap(prev => new Map(prev).set(participant.user_id, username));
        }
      }).catch(err => {
        console.warn(`Failed to fetch user info for user ${participant.user_id}:`, err);
      });

      // Return a more informative fallback while loading
      return `User ${participant.user_id}`;
    }

    // Fallback to Player X
    return `Player ${pIndex + 1}`;
  }, [userMap, fetchUserInfo]);

  // Builds a one-line summary of a session's game, participants/scores and
  // winner(s) - shared by both the per-session and per-day share text.
  const formatSessionSummaryLine = useCallback((session) => {
    const participants = Array.isArray(session.participants) ? session.participants : [];
    const participantsText = participants
      .map((p, i) => {
        const name = getParticipantDisplayName(p, i);
        const hasScore = p.score !== undefined && p.score !== null && p.score !== '';
        return hasScore ? `${name} (${p.score})` : name;
      })
      .join(', ');

    const winnerIds = Array.isArray(session.winners) ? session.winners : [];
    const winnerNames = participants
      .filter((p) => winnerIds.includes(p.user_id))
      .map((p, i) => getParticipantDisplayName(p, i));

    let line = `🎮 ${session.game_name || 'Unknown Game'}`;
    if (participantsText) line += ` — ${participantsText}`;
    if (winnerNames.length > 0) line += ` (🏆 ${winnerNames.join(', ')})`;
    return line;
  }, [getParticipantDisplayName]);

  // Share a single session: build a recap image and a public link, then let
  // the user choose between link and image sharing.
  const handleShareSession = useCallback(async (session) => {
    const sessionUrl = `${FRONTEND_URL}/session/${session.id}`;
    const summaryLine = formatSessionSummaryLine(session);
    const shareText = summaryLine;
    const caption = `${summaryLine}\n\nView this session: ${sessionUrl}`;

    let recapFile = null;
    try {
      recapFile = await buildSessionRecapImage(session, {
        filename: `${session.game_name || 'session'}-recap.png`,
      });
    } catch (err) {
      logger.warn('Failed to build session recap image:', err);
    }

    setHybridShareSheet({
      title: 'Share this session',
      linkShare: { url: sessionUrl, text: shareText },
      imageShare: {
        file: recapFile,
        text: shareText,
        caption,
      },
      analyticsEvent: 'share_session',
      analyticsMeta: { session_id: session.id, game_id: session.game_id },
    });
  }, [formatSessionSummaryLine]);

  // Share an entire day: build a collage image and a combined text summary,
  // then let the user choose between link and image sharing.
  const handleShareDay = useCallback(async (dayEntry) => {
    const sessions = Array.isArray(dayEntry.sessions) ? dayEntry.sessions : [];
    const imageUrls = sessions
      .map((s) => (s.game_id ? `${API_BASE}/games/${s.game_id}/thumbnail` : null))
      .filter(Boolean);

    let collageFile = null;
    try {
      collageFile = await buildImageCollage(imageUrls, { filename: `${dayEntry.date}-collage.png` });
    } catch (err) {
      logger.warn('Failed to build day collage image:', err);
    }

    const sessionLines = sessions.map((session, i) => `${i + 1}. ${formatSessionSummaryLine(session)}`);
    const shareText = `My plays on ${dayEntry.display_date}:\n${sessionLines.join('\n')}`;
    const caption = `${shareText}\n\nCheck out Pista! 🚀\nhttps://app.voyagerbug.com`;

    setHybridShareSheet({
      title: 'Share this day',
      linkShare: { url: 'https://app.voyagerbug.com', text: shareText },
      imageShare: {
        file: collageFile,
        text: shareText,
        caption,
      },
      analyticsEvent: 'share_day',
      analyticsMeta: { date: dayEntry.date, session_count: sessions.length },
    });
  }, [formatSessionSummaryLine]);

  // Filter timeline data based on search query and wins filter - memoized for performance
  const filteredTimelineData = React.useMemo(() => {
    const q = sessionSearchQuery.trim().toLowerCase();

    const matchesSession = (session) => {
      if (onlyWins) {
        const isWinner =
          user &&
          Array.isArray(session.winners) &&
          session.winners.includes(user.id) &&
          user.id !== 0;
        if (!isWinner) return false;
      }

      if (!q) return true;

      const gameName = String(session.game_name || "").toLowerCase();
      if (gameName.includes(q)) return true;

      const parts = Array.isArray(session.participants) ? session.participants : [];
      for (let i = 0; i < parts.length; i += 1) {
        const p = parts[i] || {};
        const displayName = getParticipantDisplayName(p, i);
        const raw = `${p.username || ""} ${p.name || ""} ${p.display_name || ""} ${displayName || ""}`.toLowerCase();
        if (raw.includes(q)) return true;
      }

      return false;
    };

    return timelineData
      .map((dayEntry) => {
        const sessions = Array.isArray(dayEntry.sessions) ? dayEntry.sessions : [];
        const filteredSessions = sessions.filter(matchesSession);
        return {
          ...dayEntry,
          sessions: filteredSessions,
          session_count: filteredSessions.length,
        };
      })
      .filter((dayEntry) => dayEntry.sessions.length > 0);
  }, [sessionSearchQuery, onlyWins, timelineData, getParticipantDisplayName, user]);

  // BubbleChart component is now imported from './BubbleChart'

  const insightsBand = (
    <div className="page-insights-band">
      <div className="page-insights-band__inner">
        <InsightCarousel
          compact={true}
          view="sessions"
          user={user}
          onOpenCurate={(message) => {
            setShowCurateSidebar(true);
            setTimeout(() => {
              if (message) setInput(message);
            }, 100);
          }}
          onShowSimilar={(game) => {
            setShowCurateSidebar(true);
            setTimeout(() => showSimilarGames(game), 100);
          }}
          onSearchSessions={(query) => {
            setSessionSearchQuery(query);
            setTimeout(() => {
              if (searchInputRef.current) {
                searchInputRef.current.focus();
                searchInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 50);
          }}
          onFilterWins={handleFilterWins}
        />
      </div>
    </div>
  );

  // Banner notification (shared across empty and populated states)
  const bannerNotification = banner.show && (
    <div style={{
      position: "fixed",
      top: "calc(80px + env(safe-area-inset-top, 0px))", // Below navigation, consistent with other banners
      left: "50%",
      transform: "translateX(-50%)", // Center horizontally
      zIndex: 9999,
      padding: "12px 20px", // Consistent with other banners
      borderRadius: "8px",
      backgroundColor: banner.type === 'success' ? '#4caf50' : // Consistent colors
                     banner.type === 'error' ? '#f44336' :
                     '#2196f3',
      color: 'white', // Consistent text color
      fontSize: '14px', // Consistent with other banners
      fontWeight: '500',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      animation: 'slideInTop 0.3s ease-out', // Consistent animation
      maxWidth: '400px', // Consistent max width
      wordWrap: 'break-word', // Consistent word wrap
      textAlign: 'center'
    }}>
      {banner.message}
    </div>
  );

  // Session logging modal (shared across empty and populated states)
  const sessionModal = showSessionModal && (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999
      }}
      onClick={() => setShowSessionModal(false)}
    >
      <div
        style={{
          backgroundColor: "var(--bg-primary, white)",
          color: "var(--text-primary, #333)",
          padding: "clamp(1rem, 3vw, 2rem)",
          borderRadius: "8px",
          maxWidth: "clamp(300px, 90vw, 600px)",
          width: "clamp(280px, 85vw, 90%)",
          maxHeight: "clamp(60dvh, 80dvh, 85dvh)",
          overflow: "auto",
          margin: "clamp(1rem, 3vw, 2rem)",
          border: "1px solid var(--border-color, #ddd)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <SessionForm
          user={user}
          sessionId={editingSession ? editingSession.id : null}
          initialGame={editingSession ? {
          id: editingSession.game_id,
          name: editingSession.game_name,
          image: editingSession.game_image
        } : (scorePadHandoff ? scorePadHandoff.game : null)}
          initialSessionData={editingSession ? {
            game: {
              id: editingSession.game_id,
              name: editingSession.game_name,
              image: editingSession.game_image
            },
            date: formatSessionDate(editingSession.chosen_at),
            chosen_at: editingSession.chosen_at,
            participants: editingSession.participants,
            notes: editingSession.notes,
            win_loss: editingSession.win_loss
          } : (scorePadHandoff ? {
            game: scorePadHandoff.game,
            participants: scorePadHandoff.participants
          } : null)}
          viewOnly={editingSession && (() => {
            // Check if user is participant but not owner
            const isOwner = editingSession.user_id === user.id || (
                           editingSession.participants &&
                           editingSession.participants.length > 0 &&
                           editingSession.participants[0].user_id === user.id
            );
            const isParticipant = editingSession.participants &&
                                 editingSession.participants.some(p => p.user_id === user.id);
            return isParticipant && !isOwner;
          })()}
          onSave={(res) => {
            // Immediate ref-based guard
            if (saveInProgress.current) {
              return;
            }

            // State-based guard (backup)
            if (isSaving) {
              return;
            }

            saveInProgress.current = true;
            setIsSaving(true);

            if (editingSession) {
              trackClick('play_log_edit', { session_id: editingSession.id, game_name: editingSession.game_name });
              showBanner("Session updated successfully!", 'success');
              setShowSessionModal(false);
              setEditingSession(null);
              setScorePadHandoff(null);
              setPagination({ limit: 50, offset: 0, hasMore: true });
              loadTimelineData();
              setTimeout(() => {
                saveInProgress.current = false;
                setIsSaving(false);
              }, 1000);
            } else {
              const source = scorePadHandoff ? 'scorepad' : 'timeline';
              trackClick('play_log', { game_name: res?.game_name, game_id: res?.chosen_game_id, source });
              trackClick('session_logged', { game_name: res?.game_name, game_id: res?.chosen_game_id, source });
              showBanner("Session logged successfully!", 'success');
              // Do NOT close the modal yet — wait for onInvitesDone so share
              // modals can run while SessionForm is still mounted.
            }
          }}
          onInvitesDone={() => {
            // Called after all invite share modals are dismissed (or immediately
            // if no invites were flagged). Safe to close and refresh now.
            setShowSessionModal(false);
            setScorePadHandoff(null);
            setPagination({ limit: 50, offset: 0, hasMore: true });
            loadTimelineData();
            setTimeout(() => {
              saveInProgress.current = false;
              setIsSaving(false);
            }, 1000);
          }}
          onCancel={() => {
            setShowSessionModal(false);
            setEditingSession(null);
            setScorePadHandoff(null);
          }}
          showBanner={showBanner}
          userMap={userMap}
          fetchUserInfo={fetchUserInfo}
        />
      </div>
    </div>
  );

  // ScorePad modal (for Score & Log flow)
  const scorePadModal = showScorePadModal && (
    <div
      className="safe-area-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999
      }}
      onClick={() => setShowScorePadModal(false)}
    >
      <div
        style={{
          backgroundColor: "var(--bg-primary, white)",
          color: "var(--text-primary, #333)",
          padding: "clamp(1rem, 3vw, 2rem)",
          borderRadius: "8px",
          maxWidth: "clamp(300px, 90vw, 600px)",
          width: "clamp(280px, 85vw, 90%)",
          maxHeight: "clamp(60dvh, 80dvh, 85dvh)",
          overflow: "auto",
          margin: "clamp(1rem, 3vw, 2rem)",
          border: "1px solid var(--border-color, #ddd)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <ScorePad
          user={user}
          onClose={() => setShowScorePadModal(false)}
          onLogSession={(data) => {
            // Hand the scored game + participants off to the standard session
            // form so the session can be reviewed (winners, notes, etc.) and
            // saved through the existing, proven save flow.
            setShowScorePadModal(false);
            setScorePadHandoff(data);
            setShowSessionModal(true);
          }}
          showBanner={showBanner}
        />
      </div>
    </div>
  );

  if (timelineData.length === 0) {
    return (
      <div className="profile-container past-sessions-page-container" style={{
        minHeight: "100dvh"
      }}>

      {/* Floating Add Session Button with sub-menu */}
      <div style={{ position: "fixed", top: "calc(170px + env(safe-area-inset-top, 0px))", right: "1.5rem", zIndex: 100 }}>
        {showAddSubMenu && (
          <div style={{
            position: "absolute",
            bottom: "60px",
            right: 0,
            backgroundColor: "var(--bg-primary, white)",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: "1px solid var(--border-color, #ddd)",
            overflow: "hidden",
            minWidth: "160px"
          }}>
            <button
              onClick={() => { setShowAddSubMenu(false); handleLogSession(); }}
              style={{
                display: "block",
                width: "100%",
                padding: "0.75rem 1rem",
                border: "none",
                background: "transparent",
                color: "var(--text-primary, #333)",
                fontSize: "0.875rem",
                cursor: "pointer",
                textAlign: "left",
                borderBottom: "1px solid var(--border-color, #eee)"
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = "var(--bg-secondary, #f5f5f5)"}
              onMouseOut={(e) => e.target.style.backgroundColor = "transparent"}
            >
              Add Session
            </button>
            <button
              onClick={() => { setShowAddSubMenu(false); handleScoreAndLog(); }}
              style={{
                display: "block",
                width: "100%",
                padding: "0.75rem 1rem",
                border: "none",
                background: "transparent",
                color: "var(--text-primary, #333)",
                fontSize: "0.875rem",
                cursor: "pointer",
                textAlign: "left"
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = "var(--bg-secondary, #f5f5f5)"}
              onMouseOut={(e) => e.target.style.backgroundColor = "transparent"}
            >
              Score & Log
            </button>
          </div>
        )}
        <button
          className="floating-add-session-btn"
          onClick={() => setShowAddSubMenu(!showAddSubMenu)}
          style={{
            backgroundColor: "var(--pista-green-primary, #2E7D32)",
            color: "var(--pista-button-text, white)",
            border: "none",
            borderRadius: "50%",
            cursor: "pointer",
            fontSize: "clamp(0.875rem, 2.5vw, 1rem)",
            fontWeight: "bold",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            width: "50px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease"
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = "var(--pista-green-hover, #388E3C)";
            e.target.style.transform = "scale(1.05)";
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = "var(--pista-green-primary, #2E7D32)";
            e.target.style.transform = "scale(1)";
          }}
          title="Add a session"
          aria-label="Add session options"
          aria-expanded={showAddSubMenu}
        >
          +
        </button>
      </div>

        <div ref={sessionsFilterRef} className="tab-content-wrapper" style={{
          width: "100vw",
          marginLeft: "calc(-50vw + 50%)",
          marginRight: "calc(-50vw + 50%)"
        }}>
          {insightsBand}
          <div className="past-sessions-empty-state" style={{ padding: "2rem", textAlign: "center" }}>
            <div className="past-sessions-empty-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
              <h2 className="past-sessions-empty-heading" style={{ margin: 0 }}>Past Sessions (0)</h2>
            </div>
            <p className="past-sessions-empty-text" style={{ color: "var(--text-secondary, #666)" }}>
              No past sessions to display yet. Start by logging your first game session!
            </p>
          </div>
        </div>
        {bannerNotification}
        {sessionModal}
        {scorePadModal}
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="profile-container past-sessions-page-container" style={{
        minHeight: "100dvh"
      }}>
        {/* Session Card Hover Styles */}
        <style>{`
          .session-card-interactive {
            transition: all 0.2s ease;
          }
          .session-card-interactive:hover {
            background-color: var(--bg-tertiary, #f5f5f5) !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }
          .session-card-static {
            cursor: default;
          }
          .remove-session-btn:hover {
            background-color: rgba(220, 53, 69, 1) !important;
          }
        `}</style>

        {bannerNotification}

      {/* Floating Add Session Button with sub-menu */}
      <div style={{ position: "fixed", top: "calc(200px + env(safe-area-inset-top, 0px))", right: "2rem", zIndex: 100 }}>
        {showAddSubMenu && (
          <div style={{
            position: "absolute",
            bottom: "60px",
            right: 0,
            backgroundColor: "var(--bg-primary, white)",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: "1px solid var(--border-color, #ddd)",
            overflow: "hidden",
            minWidth: "160px"
          }}>
            <button
              onClick={() => { setShowAddSubMenu(false); handleLogSession(); }}
              style={{
                display: "block",
                width: "100%",
                padding: "0.75rem 1rem",
                border: "none",
                background: "transparent",
                color: "var(--text-primary, #333)",
                fontSize: "0.875rem",
                cursor: "pointer",
                textAlign: "left",
                borderBottom: "1px solid var(--border-color, #eee)"
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = "var(--bg-secondary, #f5f5f5)"}
              onMouseOut={(e) => e.target.style.backgroundColor = "transparent"}
            >
              Add Session
            </button>
            <button
              onClick={() => { setShowAddSubMenu(false); handleScoreAndLog(); }}
              style={{
                display: "block",
                width: "100%",
                padding: "0.75rem 1rem",
                border: "none",
                background: "transparent",
                color: "var(--text-primary, #333)",
                fontSize: "0.875rem",
                cursor: "pointer",
                textAlign: "left"
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = "var(--bg-secondary, #f5f5f5)"}
              onMouseOut={(e) => e.target.style.backgroundColor = "transparent"}
            >
              Score & Log
            </button>
          </div>
        )}
        <button
          className="floating-add-session-btn"
          onClick={() => setShowAddSubMenu(!showAddSubMenu)}
          style={{
            backgroundColor: "var(--pista-green-primary, #2E7D32)",
            color: "var(--pista-button-text, white)",
            border: "none",
            borderRadius: "50%",
            cursor: "pointer",
            fontSize: "clamp(0.875rem, 2.5vw, 1rem)",
            fontWeight: "bold",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            width: "50px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease"
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = "var(--pista-green-hover, #388E3C)";
            e.target.style.transform = "scale(1.05)";
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = "var(--pista-green-primary, #2E7D32)";
            e.target.style.transform = "scale(1)";
          }}
          title="Add a session"
          aria-label="Add session options"
          aria-expanded={showAddSubMenu}
        >
          +
        </button>
      </div>

      <div ref={sessionsFilterRef} className="tab-content-wrapper" style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden"
      }}>
        {insightsBand}
        <div className="past-sessions-content" style={{ padding: "0 2rem 2rem 2rem" }}>
          {/* Chart Views Tabs */}
          <ChartTabsLayout
            title={(
              <div className="past-sessions-title-group">
                <h2 className="past-sessions-heading" style={{ margin: 0, fontSize: "clamp(1.2rem, 4vw, 1.5rem)", fontWeight: "bold" }}>Past Sessions</h2>
                <span className="past-sessions-count-pill" style={{
                  display: "inline-block",
                  marginTop: "0.35rem",
                  padding: "0.25rem 0.75rem",
                  backgroundColor: "var(--bg-tertiary, #e3f2fd)",
                  color: "var(--text-primary, #333)",
                  borderRadius: "12px",
                  fontSize: "0.8rem",
                  fontWeight: 500
                }}>
                  {loadedPlayCount} plays in {timelineData.length} days
                </span>
              </div>
            )}
            footer={(
              <div className="chart-range-footer" style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                <span className="chart-range-label" style={{ fontWeight: 600, color: "var(--text-secondary, #666)", fontSize: "clamp(0.8rem, 2vw, 0.9rem)", whiteSpace: "nowrap" }}>Recent plays</span>
                <input
                  className="chart-range-input"
                  type="number"
                  min="1"
                  max="999"
                  placeholder="All"
                  aria-label="Recent plays count"
                  value={chartRange === "max" ? "" : chartRange}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setChartRange("max");
                    } else {
                      const n = parseInt(value, 10);
                      if (!isNaN(n) && n > 0) setChartRange(String(n));
                    }
                  }}
                />
                <select
                  className="chart-range-mode-select"
                  aria-label="Filter recent plays by"
                  value={rangeMode}
                  onChange={(e) => setRangeMode(e.target.value)}
                  disabled={chartRange === 'max'}
                  style={{
                    padding: "clamp(0.4rem, 1.5vw, 0.55rem) clamp(0.5rem, 2vw, 0.75rem)",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color, #ddd)",
                    backgroundColor: "var(--bg-primary, white)",
                    color: "var(--text-primary, #333)",
                    fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
                    fontWeight: 600,
                    cursor: chartRange === 'max' ? "default" : "pointer",
                  }}
                >
                  <option value="sessions">sessions</option>
                  <option value="days">days</option>
                </select>
              </div>
            )}
          >
            {(activeTab) => (
              activeTab === "bubble" ? (
                <BubbleChart
                  timelineData={timelineData}
                  chartRange={chartRange}
                  rangeMode={rangeMode}
                  onShare={handleShareChart}
                  onExpand={() => openFullscreen('bubble')}
                  user={user}
                />
              ) : (
                <ActivityBubbleChart
                  timelineData={timelineData}
                  chartRange={chartRange}
                  rangeMode={rangeMode}
                  onShare={handleShareChart}
                  onExpand={() => openFullscreen('activity')}
                  user={user}
                />
              )
            )}
          </ChartTabsLayout>

          <FullscreenChartModal
            isOpen={!!fullscreenChart}
            onClose={closeFullscreen}
            onShare={() => setFullscreenShareTrigger((n) => n + 1)}
          >
            {fullscreenChart === 'bubble' ? (
              <BubbleChart
                timelineData={timelineData}
                chartRange={chartRange}
                rangeMode={rangeMode}
                onShare={handleShareChart}
                user={user}
                isFullscreen
                shareTrigger={fullscreenShareTrigger}
              />
            ) : fullscreenChart === 'activity' ? (
              <ActivityBubbleChart
                timelineData={timelineData}
                chartRange={chartRange}
                rangeMode={rangeMode}
                onShare={handleShareChart}
                user={user}
                isFullscreen
                shareTrigger={fullscreenShareTrigger}
              />
            ) : null}
          </FullscreenChartModal>

          <HybridShareSheet
            isOpen={!!hybridShareSheet}
            title={hybridShareSheet?.title}
            linkShare={hybridShareSheet?.linkShare}
            imageShare={hybridShareSheet?.imageShare}
            analyticsEvent={hybridShareSheet?.analyticsEvent}
            analyticsMeta={hybridShareSheet?.analyticsMeta}
            onClose={() => setHybridShareSheet(null)}
          />

          {/* Logged Sessions Search */}
          <div className="past-sessions-search-bar" style={{
            marginBottom: "1rem",
            display: "flex",
            gap: "0.75rem",
            alignItems: "center",
            flexWrap: "wrap",
            flexDirection: "row"
          }}>
            <input
              ref={searchInputRef}
              className="past-sessions-search-input"
              type="text"
              value={sessionSearchQuery}
              onChange={(e) => setSessionSearchQuery(e.target.value)}
              placeholder="Search sessions by game or user..."
              style={{
                flex: "1 1 200px",
                padding: "0.75rem",
                borderRadius: "8px",
                border: "1px solid var(--border-color, #ddd)",
                backgroundColor: "var(--bg-primary, white)",
                fontSize: "clamp(0.875rem, 2.5vw, 1rem)",
                minWidth: "200px"
              }}
            />
            <button
              ref={winsFilterRef}
              onClick={handleFilterWins}
              aria-pressed={onlyWins}
              style={{
                padding: "0.5rem 0.9rem",
                borderRadius: "12px",
                border: `1px solid ${onlyWins ? 'var(--pista-green-primary, #2E7D32)' : 'var(--border-color, #ddd)'}`,
                backgroundColor: onlyWins ? 'var(--pista-green-primary, #2E7D32)' : 'var(--bg-secondary, #f5f5f5)',
                color: onlyWins ? 'var(--pista-button-text, white)' : 'var(--text-primary, #333)',
                fontSize: "0.8rem",
                fontWeight: 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease",
                flexShrink: 0,
              }}
            >
              Only wins
            </button>
          </div>

          <div className="past-sessions-days">
            {filteredTimelineData
              .slice(0, showAllDays ? filteredTimelineData.length : 5)
              .map((dayEntry) => (
                <article key={dayEntry.date} className="past-sessions-day">
                  <header className="past-sessions-day__header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
                    <span>
                      {dayEntry.display_date} — {dayEntry.session_count} session
                      {dayEntry.session_count !== 1 ? "s" : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleShareDay(dayEntry)}
                      title="Share this day"
                      aria-label="Share this day"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "28px",
                        height: "28px",
                        borderRadius: "50%",
                        border: "1px solid var(--border-color, #ddd)",
                        backgroundColor: "var(--bg-primary, white)",
                        color: "var(--text-primary, #333)",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      <Share2 size={14} />
                    </button>
                  </header>
                  <div className="past-sessions-day__body">
                    <div className="past-sessions-day__grid">
                      {dayEntry.sessions.map((session) => (
                        <SessionCard
                          key={session.id}
                          session={session}
                          user={user}
                          onEdit={handleEditSession}
                          onRemove={handleRemoveSession}
                          onShare={handleShareSession}
                          getParticipantDisplayName={getParticipantDisplayName}
                          inviteStatuses={inviteStatusMap[session.id] || []}
                          onResendInvite={(inv) => {
                            setResendInvite({ ...inv, game_name: session.game_name || '' });
                            setResendModalOpen(true);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </article>
              ))}
          </div>

          {/* Show More/Less Button */}
          {timelineData.length > 5 && (
            <div className="past-sessions-show-more" style={{ textAlign: "center", marginTop: "1rem" }}>
              <button
                className="past-sessions-show-more-btn"
                onClick={() => setShowAllDays(!showAllDays)}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "var(--pista-button-bg, #1976d2)",
                  color: "var(--pista-button-text, white)",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  transition: "background-color 0.2s ease"
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "var(--primary-dark, #1565c0)";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "var(--primary-color, #1976d2)";
                }}
              >
                {showAllDays ? "Show Less" : `Show More (${timelineData.length - 5} more days)`}
              </button>
            </div>
          )}

          {/* Load More Sessions Button */}
          {pagination.hasMore && (
            <div className="past-sessions-load-more" style={{ textAlign: "center", marginTop: "1rem" }}>
              <button
                className="past-sessions-load-more-btn"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: isLoadingMore ? "var(--border-color, #ccc)" : "var(--pista-green-primary, #2E7D32)",
                  color: "var(--pista-button-text, white)",
                  border: "none",
                  borderRadius: "6px",
                  cursor: isLoadingMore ? "not-allowed" : "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  transition: "background-color 0.2s ease",
                  opacity: isLoadingMore ? 0.7 : 1
                }}
                onMouseOver={(e) => {
                  if (!isLoadingMore) {
                    e.target.style.backgroundColor = "var(--pista-green-hover, #388E3C)";
                  }
                }}
                onMouseOut={(e) => {
                  if (!isLoadingMore) {
                    e.target.style.backgroundColor = "var(--pista-green-primary, #2E7D32)";
                  }
                }}
              >
                {isLoadingMore ? "Loading..." : "Load More Sessions"}
              </button>
            </div>
          )}
        </div>
      </div>

        {sessionModal}
        {scorePadModal}

        {/* Confirm Dialog - Outside Session Modal */}
        <ConfirmModal
          isOpen={confirmDialog.show}
          title="Please confirm"
          message={confirmDialog.message}
          cancelText="Cancel"
          confirmText="Confirm"
          type="danger"
          onConfirm={() => {
            if (confirmDialog.onConfirm) {
              confirmDialog.onConfirm();
            }
            setConfirmDialog({ show: false, message: "", onConfirm: null });
          }}
          onCancel={() => setConfirmDialog({ show: false, message: "", onConfirm: null })}
        />
      </div>

    {/* Curate Sidebar */}
    <SidebarShell
      isOpen={showCurateSidebar}
      onClose={() => setShowCurateSidebar(false)}
      title="Ask Pista"
      className="curate-sidebar"
      dataTestId="curate-sidebar"
    >
      <div style={{ height: '100%' }}>
        <CurateSimplified
          user={user}
          onClose={() => setShowCurateSidebar(false)}
          ref={setCurateRef}
          sidebarMode={true}
        />
      </div>
    </SidebarShell>

    {/* Resend invite share modal — triggered by pending badge click in Timeline */}
    {resendInvite && (
      <SessionInviteShareModal
        isOpen={resendModalOpen}
        token={resendInvite.token}
        inviteUrl={`${FRONTEND_URL}/session-invite/${resendInvite.token}`}
        gameName={resendInvite.game_name || ''}
        guestName={resendInvite.guest_name}
        inviterName={user?.username || user?.name || 'You'}
        onClose={() => {
          setResendModalOpen(false);
          setResendInvite(null);
        }}
      />
    )}
    </React.Fragment>
  );
}

export default PastSessions;
